import { MenuOption } from '@movable/ui';

import {
  CampaignGroupBy,
  Channel,
  ChannelCategory,
  CreativeGroupBy,
  PredefinedDateRange,
} from '@/generated';
import { CampaignStageGroupEnumType } from '@/hooks/useStageHelper';
import {
  ExtendedPredefinedDateRangeType,
  PredefinedDateRangeLabelsType,
} from '@/pages/AnalyticsPage/analyticsTypes';

export const LIFT_THRESHOLD = 0.00005 as const;
export const CONTACT_METRICS_TIMEOUT_MS = 10_000;

// gRPC error codes surfaced by the analytics backend via GraphQL
export const ANALYTICS_ERROR_CODES = {
  TIMEOUT: 'ANALYTICS_TIMEOUT_CODE',
  CONNECTION: 'ANALYTICS_CONNECTION_ERROR',
} as const;

export const ANALYTICS_TIMEOUT_ALERT_TITLE = 'Data Load Timed Out';

export const STAGE_OPTIONS = [
  CampaignStageGroupEnumType.InProgress,
  CampaignStageGroupEnumType.Suspended,
  CampaignStageGroupEnumType.Completed,
  CampaignStageGroupEnumType.Canceled,
] as const;

export const RECURRING_STAGE_OPTIONS = [
  CampaignStageGroupEnumType.Completed,
  CampaignStageGroupEnumType.Suspended,
  CampaignStageGroupEnumType.Terminated,
  CampaignStageGroupEnumType.Canceled,
] as const;

// These "columns" are hidden because they're displayed some other way.
// Usually in the header or combined with another column.
export const analyticsHiddenColumns = [
  'experiment_id',
  'experimentMinDate',
  'experimentMaxDate',
  'experimentIsInternal',
  'autoImpressionRateLift',
  'autoImpressionRateLiftCILower',
  'autoImpressionRateLiftCIUpper',
  'autoImpressionRateLiftConfidence',
  'autoImpressionRateLiftIsStatisticallySignificant',
  'clickthroughRateLift',
  'clickthroughRateLiftCILower',
  'clickthroughRateLiftCIUpper',
  'clickthroughRateLiftConfidence',
  'clickthroughRateLiftIsStatisticallySignificant',
  'experiment_start',
  'experiment_status',
  'experiment_title',
  'impressionRateLift',
  'impressionRateLiftCILower',
  'impressionRateLiftCIUpper',
  'impressionRateLiftConfidence',
  'impressionRateLiftIsStatisticallySignificant',
  'uniqueClickRateLift',
  'uniqueClickRateLiftCILower',
  'uniqueClickRateLiftCIUpper',
  'uniqueClickRateLiftConfidence',
  'uniqueClickRateLiftIsStatisticallySignificant',
  'uniqueClickthroughRateLift',
  'uniqueClickthroughRateLiftCILower',
  'uniqueClickthroughRateLiftCIUpper',
  'uniqueClickthroughRateLiftConfidence',
  'uniqueClickthroughRateLiftIsStatisticallySignificant',
  'uniqueImpressionRateLift',
  'uniqueImpressionRateLiftCILower',
  'uniqueImpressionRateLiftCIUpper',
  'uniqueImpressionRateLiftConfidence',
  'uniqueImpressionRateLiftIsStatisticallySignificant',
  'uniqueAutoImpressionRateLift',
  'uniqueAutoImpressionRateLiftCILower',
  'uniqueAutoImpressionRateLiftCIUpper',
  'uniqueAutoImpressionRateLiftConfidence',
  'uniqueAutoImpressionRateLiftIsStatisticallySignificant',
  'uniqueOpenRateLift',
  'uniqueOpenRateLiftCILower',
  'uniqueOpenRateLiftCIUpper',
  'uniqueOpenRateLiftConfidence',
  'uniqueOpenRateLiftIsStatisticallySignificant',
  'uniqueAutoOpenRateLift',
  'uniqueAutoOpenRateLiftCILower',
  'uniqueAutoOpenRateLiftCIUpper',
  'uniqueAutoOpenRateLiftConfidence',
  'uniqueAutoOpenRateLiftIsStatisticallySignificant',
  'conversionRateLift',
  'conversionRateLiftCILower',
  'conversionRateLiftCIUpper',
  'conversionRateLiftConfidence',
  'conversionRateLiftIsStatisticallySignificant',
  'averageOrderValueLift',
  'averageOrderValueLiftCILower',
  'averageOrderValueLiftCIUpper',
  'averageOrderValueLiftConfidence',
  'averageOrderValueLiftIsStatisticallySignificant',
  'revenuePerSendLift',
  'revenuePerSendLiftCILower',
  'revenuePerSendLiftCIUpper',
  'revenuePerSendLiftConfidence',
  'revenuePerSendLiftIsStatisticallySignificant',
  'revenuePerThousandSendsLift',
  'revenuePerThousandSendsLiftCILower',
  'revenuePerThousandSendsLiftCIUpper',
  'revenuePerThousandSendsLiftConfidence',
  'revenuePerThousandSendsLiftIsStatisticallySignificant',
  'revenuePerPlacementLift',
  'revenuePerPlacementLiftCILower',
  'revenuePerPlacementLiftCIUpper',
  'revenuePerPlacementLiftConfidence',
  'revenuePerPlacementLiftIsStatisticallySignificant',
  'revenuePerThousandPlacementsLift',
  'revenuePerThousandPlacementsLiftCILower',
  'revenuePerThousandPlacementsLiftCIUpper',
  'revenuePerThousandPlacementsLiftConfidence',
  'revenuePerThousandPlacementsLiftIsStatisticallySignificant',
  'unsubscribeRateLift',
  'unsubscribeRateLiftCILower',
  'unsubscribeRateLiftCIUpper',
  'unsubscribeRateLiftConfidence',
  'unsubscribeRateLiftIsStatisticallySignificant',
  'uniqueConversionRateLift',
  'uniqueConversionRateLiftCILower',
  'uniqueConversionRateLiftCIUpper',
  'uniqueConversionRateLiftConfidence',
  'uniqueConversionRateLiftIsStatisticallySignificant',
];

export const defaultColumnWidth = 150;

const commonPredefinedRangeLabels: Record<
  string,
  Omit<PredefinedDateRangeLabelsType, 'primaryLabel'>
> = {
  weekLabels: {
    incrementDecrementLabel: 'Week Of',
    incrementTooltipLabel: 'Next Week',
    decrementTooltipLabel: 'Previous Week',
  },
  monthLabels: {
    incrementDecrementLabel: 'Month Of',
    incrementTooltipLabel: 'Next Month',
    decrementTooltipLabel: 'Previous Month',
  },
  quarterLabels: {
    incrementDecrementLabel: 'Quarter Of',
    incrementTooltipLabel: 'Next Quarter',
    decrementTooltipLabel: 'Previous Quarter',
  },
  yearLabels: {
    incrementDecrementLabel: 'Year Of',
    incrementTooltipLabel: 'Next Year',
    decrementTooltipLabel: 'Previous Year',
  },
};

export enum FrontendOnlyPredefinedDateRange {
  CustomRange = 'CustomRange',
}

export const predefinedDateRangeLabelMap = {
  [PredefinedDateRange.FiscalThisWeek]: {
    primaryLabel: 'This Week',
    ...commonPredefinedRangeLabels.weekLabels,
  },
  [PredefinedDateRange.FiscalThisMonth]: {
    primaryLabel: 'This Month',
    ...commonPredefinedRangeLabels.monthLabels,
  },
  [PredefinedDateRange.FiscalThisQuarter]: {
    primaryLabel: 'This Quarter',
    ...commonPredefinedRangeLabels.quarterLabels,
  },
  [PredefinedDateRange.FiscalThisYear]: {
    primaryLabel: 'This Year',
    ...commonPredefinedRangeLabels.yearLabels,
  },
  [PredefinedDateRange.FiscalLastWeek]: {
    primaryLabel: 'Last Week',
    ...commonPredefinedRangeLabels.weekLabels,
  },
  [PredefinedDateRange.FiscalLastMonth]: {
    primaryLabel: 'Last Month',
    ...commonPredefinedRangeLabels.monthLabels,
  },
  [PredefinedDateRange.FiscalLastQuarter]: {
    primaryLabel: 'Last Quarter',
    ...commonPredefinedRangeLabels.quarterLabels,
  },
  [PredefinedDateRange.FiscalLastYear]: {
    primaryLabel: 'Last Year',
    ...commonPredefinedRangeLabels.yearLabels,
  },
  [PredefinedDateRange.GregorianThisWeek]: {
    primaryLabel: 'This Week',
    ...commonPredefinedRangeLabels.weekLabels,
  },
  [PredefinedDateRange.GregorianThisMonth]: {
    primaryLabel: 'This Month',
    ...commonPredefinedRangeLabels.monthLabels,
  },
  [PredefinedDateRange.GregorianThisQuarter]: {
    primaryLabel: 'This Quarter',
    ...commonPredefinedRangeLabels.quarterLabels,
  },
  [PredefinedDateRange.GregorianThisYear]: {
    primaryLabel: 'This Year',
    ...commonPredefinedRangeLabels.yearLabels,
  },
  [PredefinedDateRange.GregorianLastWeek]: {
    primaryLabel: 'Last Week',
    ...commonPredefinedRangeLabels.weekLabels,
  },
  [PredefinedDateRange.GregorianLastMonth]: {
    primaryLabel: 'Last Month',
    ...commonPredefinedRangeLabels.monthLabels,
  },
  [PredefinedDateRange.GregorianLastQuarter]: {
    primaryLabel: 'Last Quarter',
    ...commonPredefinedRangeLabels.quarterLabels,
  },
  [PredefinedDateRange.GregorianLastYear]: {
    primaryLabel: 'Last Year',
    ...commonPredefinedRangeLabels.yearLabels,
  },
  [PredefinedDateRange.Today]: {
    primaryLabel: 'Today',
    incrementDecrementLabel: 'Day Of',
    incrementTooltipLabel: 'Next Day',
    decrementTooltipLabel: 'Previous Day',
  },
  [PredefinedDateRange.Yesterday]: {
    primaryLabel: 'Yesterday',
    incrementDecrementLabel: 'Day Of',
    incrementTooltipLabel: 'Next Day',
    decrementTooltipLabel: 'Previous Day',
  },
  [PredefinedDateRange.Last_3Days]: {
    primaryLabel: 'Last 3 Days',
    incrementDecrementLabel: '3-Day',
    incrementTooltipLabel: 'Next 3 Days',
    decrementTooltipLabel: 'Previous 3 Days',
  },
  [PredefinedDateRange.Last_7Days]: {
    primaryLabel: 'Last 7 Days',
    incrementDecrementLabel: '7-Day',
    incrementTooltipLabel: 'Next 7 Days',
    decrementTooltipLabel: 'Previous 7 Days',
  },
  [PredefinedDateRange.Last_30Days]: {
    primaryLabel: 'Last 30 Days',
    incrementDecrementLabel: '30-Day',
    incrementTooltipLabel: 'Next 30 Days',
    decrementTooltipLabel: 'Previous 30 Days',
  },
  CustomRange: {
    primaryLabel: 'Custom',
    incrementDecrementLabel: 'Custom',
    incrementTooltipLabel: 'Next',
    decrementTooltipLabel: 'Previous',
  },
} as const satisfies Record<
  ExtendedPredefinedDateRangeType,
  PredefinedDateRangeLabelsType
>;

// Grouping related constants and types
type CampaignGroupingColumnsValueType =
  | 'tagsGrouped'
  | 'tagsIndividualGrouped'
  | 'dayGrouped'
  | 'weekGrouped'
  | 'monthGrouped'
  | 'quarterGrouped'
  | 'yearGrouped';

type CreativeGroupingColumnsValueType = 'tagsGrouped' | 'tagsIndividualGrouped';
export type CampaignGroupingOptionsType =
  | CampaignGroupBy.Day
  | CampaignGroupBy.Week
  | CampaignGroupBy.Month
  | CampaignGroupBy.Quarter
  | CampaignGroupBy.Year
  | CampaignGroupBy.Tags
  | CampaignGroupBy.TagsIndividual;
export const CAMPAIGN_GROUPING_COLUMNS: Partial<
  Record<CampaignGroupBy, CampaignGroupingColumnsValueType>
> = {
  [CampaignGroupBy.Tags]: 'tagsGrouped',
  [CampaignGroupBy.TagsIndividual]: 'tagsIndividualGrouped',
  [CampaignGroupBy.Day]: 'dayGrouped',
  [CampaignGroupBy.Week]: 'weekGrouped',
  [CampaignGroupBy.Month]: 'monthGrouped',
  [CampaignGroupBy.Quarter]: 'quarterGrouped',
  [CampaignGroupBy.Year]: 'yearGrouped',
} as const;

export const CREATIVE_GROUPING_COLUMNS: Partial<
  Record<CreativeGroupBy, CreativeGroupingColumnsValueType>
> = {
  [CreativeGroupBy.Tags]: 'tagsGrouped',
  [CreativeGroupBy.TagsIndividual]: 'tagsIndividualGrouped',
};

export const campaignMetricSelectOptions = [
  { value: 'sends', label: 'Sends' },
  { value: 'uniqueOpens', label: 'Opens' },
  { value: 'uniqueClicks', label: 'Clicks' },
  { value: 'revenue', label: 'Revenue' },
] as MenuOption[];

export const creativeComparisonMetricSelectOptions = [
  { value: 'placements', label: 'Placements' },
  { value: 'uniqueImpressions', label: 'Impressions' },
  { value: 'uniqueConversions', label: 'Conversions' },
  { value: 'uniqueClicks', label: 'Clicks' },
  { value: 'revenue', label: 'Revenue' },
] as MenuOption[];

export const SCORE_METRIC_TO_BUCKET_FIELD: Record<string, string> = {
  creative_effectiveness: 'creativeBucket',
  open_effectiveness: 'openBucket',
  click_effectiveness: 'clickBucket',
  conversion_effectiveness: 'conversionBucket',
  revenue_effectiveness: 'revenueBucket',
  retention_effectiveness: 'retentionBucket',
};

export type MetricCardDefinition = {
  id: string;
  label: string;
};

export const CHANNEL_CATEGORY_CHANNELS: Record<ChannelCategory, Channel[]> = {
  [ChannelCategory.Email]: [Channel.Email],
  [ChannelCategory.MobileApp]: [Channel.Push, Channel.InApp, Channel.AppInbox],
  [ChannelCategory.TextMessage]: [Channel.Sms, Channel.Mms],
};

export const CHANNEL_CATEGORY_DEFAULTS: Record<ChannelCategory, Channel> = {
  [ChannelCategory.Email]: Channel.Email,
  [ChannelCategory.MobileApp]: Channel.Push,
  [ChannelCategory.TextMessage]: Channel.Sms,
};

export const CHANNEL_CATEGORY_DISPLAY_LABELS: Record<ChannelCategory, string> =
  {
    [ChannelCategory.Email]: 'Email',
    [ChannelCategory.MobileApp]: 'Mobile App',
    [ChannelCategory.TextMessage]: 'Text Messaging',
  };

export const TEXT_MESSAGING_HIDDEN_CREATIVE_METRIC_IDS: string[] = [
  'placements',
  'impressions',
  'unique_impressions',
  'impression_rate',
  'unique_impression_rate',
  'auto_impressions',
  'unique_auto_impressions',
  'auto_impression_rate',
  'unique_auto_impression_rate',
  'creative_effectiveness',
  'open_effectiveness',
  'click_effectiveness',
  'conversion_effectiveness',
  'aov_effectiveness',
  'retention_effectiveness',
  'positive_reaction',
];

export const CHANNEL_DISPLAY_LABELS: Record<Channel, string> = {
  [Channel.AppInbox]: 'App Inbox',
  [Channel.Email]: 'Email',
  [Channel.InApp]: 'In-App',
  [Channel.Mms]: 'MMS',
  [Channel.Push]: 'Push',
  [Channel.Rcs]: 'RCS',
  [Channel.Sms]: 'SMS',
};

export const ANALYTICS_PLACEHOLDERS = {
  dashboard: 'Ask about your Dashboard Analytics',
  campaigns: 'Ask about your Campaign Analytics',
  content: 'Ask about your Content Analytics',
  audiences: 'Ask about your Audience Analytics',
  creatives: 'Ask about your Creative Analytics',
  subjectLines: 'Ask about your Subject Line Analytics',
  segmentGroups: 'Ask about your Segment Group Analytics',
  experiments: 'Ask about your Experiment Analytics',
} as const;

export const ANALYTICS_PLACEHOLDER_ROUTES = [
  {
    pattern: '/:clientId/analytics/dashboard/*',
    placeholder: ANALYTICS_PLACEHOLDERS.dashboard,
  },
  {
    pattern: '/:clientId/analytics/campaigns-v2/*',
    placeholder: ANALYTICS_PLACEHOLDERS.campaigns,
  },
  {
    pattern: '/:clientId/analytics/content/*',
    placeholder: ANALYTICS_PLACEHOLDERS.content,
  },
  {
    pattern: '/:clientId/analytics/audiences/*',
    placeholder: ANALYTICS_PLACEHOLDERS.audiences,
  },
  {
    pattern: '/:clientId/analytics/creatives-v2/*',
    placeholder: ANALYTICS_PLACEHOLDERS.creatives,
  },
  {
    pattern: '/:clientId/analytics/subject-lines-v2/*',
    placeholder: ANALYTICS_PLACEHOLDERS.subjectLines,
  },
  {
    pattern: '/:clientId/analytics/segments-v2/*',
    placeholder: ANALYTICS_PLACEHOLDERS.segmentGroups,
  },
  {
    pattern: '/:clientId/analytics/experiments-v2/*',
    placeholder: ANALYTICS_PLACEHOLDERS.experiments,
  },
] as const;
